import { createStore } from 'zustand/vanilla';

import { CreateEmployee, Department, EmployeeResponse, OrganizationSummary, EmployeeRole } from '@MP/shared/api/hooks/auth';
import ReplaceUtils from '@MP/shared/utils/ReplaceUtils';

import { AutoDepartment, OrgItem } from '../types';
import { OrganizationItem } from '../utils/OrganizationItem';

const defaultDepartmentName: string[] = ['정신건강의학과', '신경정신과'];

export interface SignUpState {
  organizations: OrganizationItem[];
  selectedOrganization: OrgItem | null;
  employee: CreateEmployee;
  confirmPassword: string;
  department: AutoDepartment | null;
  existDepartments: AutoDepartment[];
  certNumber: string;
  certId: number | null;
  certOK: boolean | null;
  isMaster: boolean;
  createdEmployee: EmployeeResponse | null;
  eulaAgree: boolean;
  privacyAgree: boolean;
  setAllAgree: (check: boolean) => void;
  setEulaAgree: (check: boolean) => void;
  setPrivacyAgree: (check: boolean) => void;
  setSubscribeMarketingEvent: (check: boolean) => void;
  setEmail: (email: string) => void;
  setName: (name: string) => void;
  setPassword: (pw: string, confirm: string) => void;
  setDepartment: (department: AutoDepartment | null) => void;
  setTel: (tel: string) => void;
  setCertNumber: (certNumber: string) => void;
  setOrganizations: (organizations: OrganizationSummary[]) => void;
  setDepartments: (departments: Department[] | null) => void;
  setSelectedOrganization: (organization: OrgItem | null) => void;
  setCertId: (certId: number) => void;
  setCertOK: (ok: boolean | null) => void;
  setCreatedEmployee: (createdEmployee: EmployeeResponse) => void;
}

export const createSignUpStore = () =>
  createStore<SignUpState>((set, get) => ({
    organizations: [],
    selectedOrganization: null,
    employee: {},
    confirmPassword: '',
    department: null,
    existDepartments: [],
    certNumber: '',
    certId: null,
    certOK: null,
    isMaster: false,
    createdEmployee: null,
    eulaAgree: false,
    privacyAgree: false,
    setAllAgree: (check) => {
      get().setEulaAgree(check);
      get().setPrivacyAgree(check);
      get().setSubscribeMarketingEvent(check);
    },
    setEulaAgree: (check) => set({ eulaAgree: check }),
    setPrivacyAgree: (check) => set({ privacyAgree: check }),
    setSubscribeMarketingEvent: (check) => {
      set({ employee: { ...get().employee, isSubscribeMarketingEvent: check } });
    },
    setEmail: (email) => {
      set({ employee: { ...get().employee, email: email.trim() } });
    },
    setName: (name) => {
      set({ employee: { ...get().employee, name: name.trim() } });
    },
    setPassword: (pw, confirm) => {
      set({ employee: { ...get().employee, password: pw.trim() }, confirmPassword: confirm.trim() });
    },
    setDepartment: (department) => {
      if (department) {
        department.add = false;
      }
      set({ department });
    },
    setTel: (tel) => {
      set({ employee: { ...get().employee, tel: ReplaceUtils.replaceNumber(tel) } });
      get().setCertOK(null);
    },
    setCertNumber: (certNumber) => {
      set({ certNumber: ReplaceUtils.replaceNumber(certNumber) });
      get().setCertOK(null);
    },
    setOrganizations: (organizations) => {
      get().setSelectedOrganization(null);
      set({ organizations: organizations.map((org) => new OrganizationItem(org)) });
    },
    setDepartments: (departments) => {
      let existDepartments: AutoDepartment[];
      if (departments) {
        existDepartments = departments;

        const names = existDepartments.map((v) => v.name);

        defaultDepartmentName.forEach((v) => {
          if (!names.includes(v)) {
            existDepartments.push({ name: v });
          }
        });
      } else {
        existDepartments = defaultDepartmentName.map((v) => {
          return { name: v };
        });
      }
      set({ existDepartments });
      get().setDepartment(existDepartments[0]);
    },
    setSelectedOrganization: (organization) => {
      set({ selectedOrganization: organization });
      get().setDepartment(null);
    },
    setCertId: (certId) => set({ certId }),
    setCertOK: (ok) => set({ certOK: ok }),
    setCreatedEmployee: (createdEmployee) => {
      set({ createdEmployee, isMaster: createdEmployee?.role === EmployeeRole.UAdmin });
    },
  }));

export type SignUpStoreApi = ReturnType<typeof createSignUpStore>;

export const selectIsRequiredAllAgree = (s: SignUpState): boolean => s.eulaAgree && s.privacyAgree;

export const selectSubscribeMarketingEvent = (s: SignUpState) => s.employee.isSubscribeMarketingEvent;

export const selectIsAllAgree = (s: SignUpState): boolean =>
  selectIsRequiredAllAgree(s) && !!selectSubscribeMarketingEvent(s);

export const selectCreationEmployeeModel = (s: SignUpState) => {
  const model = { ...s.employee };
  model.orgzId = s.department?.orgzId;
  const organizationRequest = s.selectedOrganization?.getRequest();
  model.organization = organizationRequest
    ? { ...organizationRequest, department: s.department?.name || '' }
    : undefined;
  return model;
};

export const selectIsPossibleSignup = (s: SignUpState): boolean =>
  !!(
    s.selectedOrganization &&
    s.certId &&
    s.certNumber &&
    s.certOK &&
    s.employee.password === s.confirmPassword
  );
